import {
  CircleIcon,
  LineSegmentIcon,
  MinusIcon,
  PlusIcon,
  RectangleIcon,
  StarIcon,
  TriangleIcon,
} from '@phosphor-icons/react';

import { ZOrderButtons } from './ZOrderButtons';
import { isShapeElement, useTextEditorStore } from './useTextEditorStore';

import { ColorPicker } from '@/components/ui/color-picker';
import { Slider } from '@/components/ui/slider';

interface ShapePropertyBarProps {
  elementId: string;
}

/** Resolve a ShapeElement fill to a simple hex color string for the ColorPicker. */
function resolveShapeFillColor(fill: string | { type: string; colors: [string, string] }): string {
  return typeof fill === 'string' ? fill : fill.colors[0];
}

const SHAPE_ICONS = {
  rectangle: RectangleIcon,
  circle: CircleIcon,
  ellipse: CircleIcon,
  star: StarIcon,
  triangle: TriangleIcon,
  line: LineSegmentIcon,
} as const;

const SHAPE_LABELS = {
  rectangle: 'Rectangle',
  circle: 'Circle',
  ellipse: 'Ellipse',
  star: 'Star',
  triangle: 'Triangle',
  line: 'Line',
} as const;

export function ShapePropertyBar({ elementId }: ShapePropertyBarProps) {
  const element = useTextEditorStore((s) => s.elements.find((el) => el.id === elementId));
  const updateElement = useTextEditorStore((s) => s.updateElement);

  if (!element || !isShapeElement(element)) return null;

  const ShapeIcon = SHAPE_ICONS[element.shapeType];
  const fillColor = resolveShapeFillColor(element.fill);

  return (
    <div className="flex h-10 items-center gap-3 overflow-x-auto px-3 scrollbar-hide">
      {/* Shape type indicator */}
      <div className="flex shrink-0 items-center gap-1.5 text-muted-foreground">
        <ShapeIcon size={14} />
        <span className="text-detail">{SHAPE_LABELS[element.shapeType]}</span>
      </div>

      <div className="h-4 w-px shrink-0 bg-border/40" />

      {/* Fill color */}
      <div className="shrink-0">
        <ColorPicker
          color={fillColor}
          onChange={(color) => updateElement(elementId, { fill: color })}
        />
      </div>

      <div className="h-4 w-px shrink-0 bg-border/40" />

      {/* Opacity */}
      <div className="flex shrink-0 items-center gap-2">
        <span className="shrink-0 text-detail text-muted-foreground">Opacity</span>
        <Slider
          aria-label="Opacity"
          value={[element.opacity]}
          min={0}
          max={100}
          step={1}
          className="w-20"
          onValueChange={([v]) => updateElement(elementId, { opacity: v }, { skipHistory: true })}
          onValueCommit={([v]) => updateElement(elementId, { opacity: v })}
        />
        <span className="w-8 shrink-0 text-right text-detail tabular-nums text-muted-foreground">
          {Math.round(element.opacity)}%
        </span>
      </div>

      <div className="h-4 w-px shrink-0 bg-border/40" />

      {/* Feather */}
      <div className="flex shrink-0 items-center gap-2">
        <span className="shrink-0 text-detail text-muted-foreground">Feather</span>
        <Slider
          aria-label="Feather"
          value={[element.feather]}
          min={0}
          max={100}
          step={1}
          className="w-20"
          onValueChange={([v]) => updateElement(elementId, { feather: v }, { skipHistory: true })}
          onValueCommit={([v]) => updateElement(elementId, { feather: v })}
        />
        <span className="w-8 shrink-0 text-right text-detail tabular-nums text-muted-foreground">
          {Math.round(element.feather)}%
        </span>
      </div>

      {/* Corner radius — rectangles only */}
      {element.shapeType === 'rectangle' && (
        <>
          <div className="h-4 w-px shrink-0 bg-border/40" />
          <div className="flex shrink-0 items-center gap-2">
            <span className="shrink-0 text-detail text-muted-foreground">Radius</span>
            <Slider
              aria-label="Corner radius"
              value={[element.cornerRadius ?? 0]}
              min={0}
              max={100}
              step={1}
              className="w-20"
              onValueChange={([v]) =>
                updateElement(elementId, { cornerRadius: v }, { skipHistory: true })
              }
              onValueCommit={([v]) => updateElement(elementId, { cornerRadius: v })}
            />
            <span className="w-8 shrink-0 text-right text-detail tabular-nums text-muted-foreground">
              {Math.round(element.cornerRadius ?? 0)}%
            </span>
          </div>
        </>
      )}

      {/* Star points stepper — stars only */}
      {element.shapeType === 'star' && (
        <>
          <div className="h-4 w-px shrink-0 bg-border/40" />
          <div className="flex shrink-0 items-center gap-1">
            <span className="shrink-0 text-detail text-muted-foreground">Points</span>
            <button
              type="button"
              onClick={() =>
                updateElement(elementId, { points: Math.max(3, (element.points ?? 5) - 1) })
              }
              aria-label="Decrease star points"
              className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <MinusIcon size={12} weight="bold" />
            </button>
            <span className="w-5 text-center text-detail tabular-nums">
              {element.points ?? 5}
            </span>
            <button
              type="button"
              onClick={() =>
                updateElement(elementId, { points: Math.min(12, (element.points ?? 5) + 1) })
              }
              aria-label="Increase star points"
              className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <PlusIcon size={12} weight="bold" />
            </button>
          </div>
        </>
      )}

      <div className="h-4 w-px shrink-0 bg-border/40" />

      {/* Z-order buttons */}
      <div className="shrink-0">
        <ZOrderButtons elementId={elementId} />
      </div>
    </div>
  );
}
